import { useEffect, useMemo, useState } from 'react'
import Layout from 'containers/Layout'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import Selection from 'components/molecules/Selection'
import notify from 'components/atoms/Notify'
import { getLocalizedCountries } from 'pages/company/modals/countries'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { forEach, includes, keys, split, startsWith, toLower, trim } from 'lodash'

const initialForm = {
  officeName: '',
  commercialRegisterName: '',
  commercialRegisterNumber: '',
  ownerName: '',
  email: '',
  ownerIdNumber: '',
  supervisingEmployeeName: '',
  phoneNumber: '',
  postbox: '',
  postcode: '',
  country: '',
  governorate: '',
  state: '',
  area: '',
  subscriptionType: 'package_1',
  registeredForVat: false,
  minimumPriceAllowedForPromotion: '',
  tenantLowestAge: '',
  tenantHighestAge: '',
  deliveryPrice: '',
}
const editableLeadKeys = keys(initialForm)

const sanitizeLeadPayload = payload =>
  editableLeadKeys.reduce((acc, key) => {
    if (payload?.[key] !== undefined) acc[key] = payload[key]
    return acc
  }, {})

const AddEditLead = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams()
  const history = useHistory()
  const [form, setForm] = useState(initialForm)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [countrySearch, setCountrySearch] = useState('')
  const phoneField = 'phoneNumber'
  const numericFields = ['commercialRegisterNumber', 'ownerIdNumber', 'postbox', 'postcode', 'tenantLowestAge', 'tenantHighestAge']
  const decimalFields = ['minimumPriceAllowedForPromotion', 'deliveryPrice']

  useEffect(() => {
    if (!id) return
    const load = async () => {
      const res = await apiGet(`crm/leads/${id}`)
      if (!res?.success || !res?.data) {
        history.push('/crm/leads')
        return
      }
      setForm(prev => ({ ...prev, ...sanitizeLeadPayload(res.data) }))
    }
    load()
  }, [id])

  const validateForm = values => {
    const nextErrors = {}
    // eslint-disable-next-line
    fields.forEach(key => {
      const value = values[key]
      // eslint-disable-next-line
      if (value === undefined || value === null || String(value).trim() === '') {
        nextErrors[key] = t('common.required')
      }
    })
    // eslint-disable-next-line
    const emailValue = String(values.email || '').trim()
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue)) {
      nextErrors.email = t('auth.invalidEmail')
    }
    // eslint-disable-next-line
    numericFields.forEach(key => {
      // eslint-disable-next-line
      const value = String(values[key] || '').trim()
      if (value && !/^\d+$/.test(value)) {
        nextErrors[key] = t('common.required')
      }
    })
    const phoneValue = trim(String(values[phoneField] || ''))
    if (phoneValue && !/^\+?\d+$/.test(phoneValue)) {
      nextErrors[phoneField] = t('common.required')
    }
    forEach(decimalFields, key => {
      const value = trim(String(values[key] || ''))
      if (value && !/^\d+(\.\d+)?$/.test(value)) {
        nextErrors[key] = t('common.required')
      }
    })

    if (!values.subscriptionType) {
      nextErrors.subscriptionType = t('common.required')
    }

    return nextErrors
  }

  const normalizeApiErrors = res => {
    const nextErrors = {}
    const details = res?.error?.details || res?.errors || res?.data?.error?.details || []
    // eslint-disable-next-line
    if (Array.isArray(details)) {
      // eslint-disable-next-line
      details.forEach(item => {
        const pathKey = item?.path?.[0] || item?.context?.key
        if (pathKey && !nextErrors[pathKey]) {
          nextErrors[pathKey] = item?.message || res?.message || t('common.somethingWentWrong')
        }
      })
    }
    return nextErrors
  }

  const submit = async e => {
    e.preventDefault()
    const nextErrors = validateForm(form)
    setErrors(nextErrors)
    // eslint-disable-next-line
    if (Object.keys(nextErrors).length > 0) return
    setLoading(true)
    const payload = sanitizeLeadPayload(form)
    const res = id ? await apiPut(`crm/leads/${id}`, payload) : await apiPost('crm/leads', payload)
    setLoading(false)
    if (res?.success) {
      history.push('/crm/leads')
      return
    }

    const apiErrors = normalizeApiErrors(res)
    // eslint-disable-next-line
    if (Object.keys(apiErrors).length > 0) {
      setErrors(apiErrors)
      return
    }

    if (res?.message) notify.error(res.message)
  }

  const onChange = (key, value) =>
    setForm(prev => {
      // eslint-disable-next-line
      let nextValue = value
      if (includes(numericFields, key)) {
        nextValue = String(value).replace(/\D/g, '')
      } else if (key === phoneField) {
        const rawValue = String(value)
        const hasLeadingPlus = startsWith(rawValue, '+')
        const digitsOnly = rawValue.replace(/\D/g, '')
        nextValue = hasLeadingPlus ? `+${digitsOnly}` : digitsOnly
      } else if (includes(decimalFields, key)) {
        nextValue = String(value).replace(/[^0-9.]/g, '')
        const [integer = '', ...rest] = split(nextValue, '.')
        nextValue = rest.length ? `${integer}.${rest.join('')}` : integer
      }
      const nextForm = { ...prev, [key]: nextValue }
      // eslint-disable-next-line
      if (Object.keys(errors).length > 0) {
        setErrors(validateForm(nextForm))
      }
      return nextForm
    })

  const fields = [
    'officeName',
    'commercialRegisterName',
    'commercialRegisterNumber',
    'ownerName',
    'email',
    'ownerIdNumber',
    'supervisingEmployeeName',
    'phoneNumber',
    'postbox',
    'postcode',
    'country',
    'governorate',
    'state',
    'area',
    'minimumPriceAllowedForPromotion',
    'tenantLowestAge',
    'tenantHighestAge',
    'deliveryPrice',
  ]

  const subscriptionOptions = useMemo(
    () => [
      { id: 'package_1', badge: '1', label: t('crm.packages.package_1') },
      { id: 'package_2', badge: '2', label: t('crm.packages.package_2') },
      { id: 'package_3', badge: '3', label: t('crm.packages.package_3') },
    ],
    [t]
  )

  const vatOptions = useMemo(
    () => [
      { id: 'true', name: t('common.yes') },
      { id: 'false', name: t('common.no') },
    ],
    [t]
  )

  const countryOptions = useMemo(() => {
    const language = startsWith(i18n?.language, 'ar') ? 'ar' : 'en'
    const localizedCountries = getLocalizedCountries(language).map(country => ({
      id: country.name,
      name: country.name,
    }))

    if (!trim(countrySearch)) return localizedCountries
    const query = toLower(trim(countrySearch))
    return localizedCountries.filter(country => includes(toLower(country.name), query))
  }, [countrySearch, i18n?.language])

  return (
    <Layout
      title={id ? t('crm.actions.edit') : t('crm.actions.add')}
      headerRight={
        <div className="mt-4 flex sm:mt-0 sm:ml-4 gap-3">
          <button
            onClick={() => history.push('/crm/leads')}
            type="button"
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white hover:text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-1 sm:ml-3"
          >
            {t('addEditPartner.buttons.goBack')}
          </button>
        </div>
      }
    >
      <div className="w-full mb-[60px] pb-8 lg:pb-0 flex flex-col bg-white">
        <form id="crm-lead-form" onSubmit={submit} className="space-y-8 py-8 w-full mx-auto px-8">
          <div>
            {/* <div className="border-b border-gray-200 pb-5 sm:pb-0">
              <h3 className="text-xl font-medium leading-6 text-gray-900">{id ? t('crm.actions.edit') : t('crm.actions.add')}</h3>
            </div> */}

            <div className="space-y-6 sm:space-y-5 pt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fields.map(key => (
                  <div key={key}>
                    <label htmlFor={key} className="block text-sm font-normal text-gray-700 mb-1">
                      {t(`crm.labels.${key}`)} <span className="text-red-500">*</span>
                    </label>
                    {key === 'country' ? (
                      <Selection
                        className="w-full"
                        value={form.country}
                        onChange={option => onChange('country', option?.id || '')}
                        list={countryOptions}
                        title={t('crm.labels.country')}
                        showSearch
                        onSearch={setCountrySearch}
                        searchPlaceholder={t('common.search')}
                      />
                    ) : (
                      <input
                        id={key}
                        type={key === 'email' ? 'email' : 'text'}
                        // eslint-disable-next-line
                        inputMode={includes(numericFields, key) || includes(decimalFields, key) || key === phoneField ? 'numeric' : undefined}
                        value={form[key]}
                        onChange={e => onChange(key, e.target.value)}
                        className="w-full block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                        placeholder={t(`crm.labels.${key}`)}
                      />
                    )}
                    {errors[key] && <p className="mt-1 text-sm text-red-600">{errors[key]}</p>}
                  </div>
                ))}

                <div>
                  <label className="block text-sm font-normal text-gray-700 mb-1">
                    {t('crm.columns.subscriptionType')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    {subscriptionOptions.map(option => {
                      const isSelected = form.subscriptionType === option.id
                      return (
                        <label
                          key={option.id}
                          htmlFor={`subscription-${option.id}`}
                          className={`cursor-pointer rounded-lg border p-4 transition-colors ${isSelected ? 'border-dark-500 bg-dark-50' : 'border-gray-300 hover:bg-gray-50'}`}
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <span
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium ${
                                isSelected ? 'border-dark-600 bg-dark-600 text-white' : 'border-gray-300 bg-white text-gray-600'
                              }`}
                            >
                              {option.badge}
                            </span>
                            <input
                              id={`subscription-${option.id}`}
                              type="radio"
                              name="subscriptionType"
                              value={option.id}
                              checked={isSelected}
                              onChange={e => onChange('subscriptionType', e.target.value)}
                              className="h-4 w-4 border-gray-300 accent-dark-600 focus:ring-dark-500"
                            />
                          </div>
                          <p className={`text-sm ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>{option.label}</p>
                        </label>
                      )
                    })}
                  </div>
                  {errors.subscriptionType && <p className="mt-1 text-sm text-red-600">{errors.subscriptionType}</p>}
                </div>

                <div>
                  <label className="block text-sm font-normal text-gray-700 mb-1">{t('crm.labels.registeredForVat')}</label>
                  <Selection
                    className="w-full"
                    value={String(form.registeredForVat)}
                    onChange={option => onChange('registeredForVat', option?.id === 'true')}
                    list={vatOptions}
                    title={t('crm.labels.registeredForVat')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 pb-2">
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <button
                type="button"
                onClick={() => history.push('/crm/leads')}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm text-normal rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                  loading ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                {loading ? (
                  <span className="flex items-start">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>{t('carProfile.buttons.pleaseWait')}</span>
                  </span>
                ) : (
                  <span>{id ? t('common.update') : t('common.create')}</span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  )
}

export default AddEditLead
